import { useCallback, useEffect, useState } from 'react';

import { getWindow } from '../../../services/windowService';
import { makeObservable } from '../../../utils/makeObservable';
import { addScriptToDocument } from './addScriptToDocument';
import { getScriptForLocale } from './getScriptForLocale';

const wnd = getWindow();

const categoriesHolder = makeObservable<string[]>([]);
const scriptLoaded = makeObservable<boolean>(false);

export const useTagCommander = () => {
  /* local states */
  const [loaded] = useState<boolean>(scriptLoaded.get);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(categoriesHolder.get());

  /* subscribe to updates */
  useEffect(() => {
    return categoriesHolder.subscribe(setSelectedCategories);
  }, []);

  /* export function to load the script */
  const loadTagCommanderScript = useCallback((country: string, language: string) => {
    const uri = getScriptForLocale(country, language);
    addScriptToDocument(uri).then((result) => {
      if (result) {
        scriptLoaded.set(true);
        startListener();
      }
    });
  }, []);

  /* export function to show banner */
  const showCookieBanner = useCallback((fallbackUrl: string) => {
    const showFn = (wnd as any).tC?.privacy?.showPrivacyCenter;
    if (typeof showFn === 'function') {
      showFn();
    } else {
      wnd.open(fallbackUrl);
    }
  }, []);

  return {
    loadTagCommanderScript,
    loaded,
    selectedPrivacyCategories: selectedCategories || [],
    showCookieBanner,
  };
};

const isReady = (tC: any) => {
  const getCategoriesFn = tC?.privacy?.getOptinCategories;
  const addEventListenerFn = tC?.eventTarget?.addEventListener;
  return typeof getCategoriesFn === 'function' && typeof addEventListenerFn === 'function';
};

let counter = 0;
const startListener = () => {
  const { tC } = wnd as any;
  if (!isReady(tC)) {
    counter++;
    if (counter > 100) {
      console.error('Tag Commands script is not possible to initialize');
      return;
    }
    setTimeout(startListener, 500);
    return;
  }

  const updateCategories = () => {
    const categories = tC?.privacy?.getOptinCategories() || [];
    categoriesHolder.set([...categories]);
  };
  updateCategories();

  tC.eventTarget.addEventListener('consent-update', updateCategories);
  scriptLoaded.subscribe((value) => {
    if (!value) {
      tC.eventTarget.removeEventListener('consent-update', updateCategories);
    }
  });
};
